import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  VStack,
  HStack,
  Text,
  Link,
  useToast,
  Heading,
  Divider,
  FormErrorMessage,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useThemeContext } from '@/contexts/ThemeContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { useApi } from '@/contexts/ApiContext';

export const PasswordResetPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const isResetMode = !!token;

  const { colorMode } = useThemeContext();
  const toast = useToast();
  const navigate = useNavigate();
  const api = useApi();

  const isDark = colorMode === 'dark';

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!isResetMode) {
      if (!email) newErrors.email = 'Email is required';
    } else {
      if (!password) newErrors.password = 'Password is required';
      if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
      if (password !== confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (password && password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setIsLoading(true);
    try {
      await api.UserApi.requestPasswordReset({ email });
      toast({
        title: 'Reset link sent!',
        description: 'Check your email for the password reset link.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Reset failed',
        description: error?.response?.data?.message || error.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await api.UserApi.resetPassword({
        token: token!,
        new_password: password,
        password_confirmation: confirmPassword
      });
      
      toast({
        title: 'Password reset successful!',
        description: 'Your password has been updated. You can now sign in.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      navigate('/login');
    } catch (error: any) {
      toast({
        title: 'Password reset failed',
        description: error.message || 'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field === 'email') setEmail(value);
    if (field === 'password') setPassword(value);
    if (field === 'confirmPassword') setConfirmPassword(value);
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Box 
      minH="100vh" 
      bg={isDark ? 'gray.900' : 'gray.50'}
      bgGradient={isDark 
        ? 'linear(to-br, gray.900, blue.900, purple.900)' 
        : 'linear(to-br, blue.50, purple.50, pink.50)'
      }
      py={8}
      position="relative"
    >
      {/* Theme Toggle */}
      <Box position="absolute" top={4} right={4}>
        <ThemeToggle size="md" variant="outline" />
      </Box>
      
      <Container maxW="md" centerContent>
        <VStack spacing={8} w="full">
          {/* Logo/Brand */}
          <VStack spacing={2}>
            <Heading 
              size="xl" 
              bgGradient="linear(to-r, blue.400, purple.500, pink.500)"
              bgClip="text"
              fontWeight="bold"
            >
              Casino Pro
            </Heading>
            <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="lg">
              {isResetMode ? 'Create your new password' : 'Reset your password'}
            </Text>
          </VStack>

          <Card 
            w="full" 
            bg={isDark ? 'gray.800' : 'white'}
            shadow="xl" 
            borderRadius="2xl"
            border="1px"
            borderColor={isDark ? 'gray.600' : 'gray.200'}
          >
            <CardBody p={8}>
              {!isResetMode && !isEmailSent && (
                <form onSubmit={handlePasswordReset}>
                  <VStack spacing={6}>
                    <VStack spacing={4} w="full">
                      <FormControl isRequired isInvalid={!!errors.email}>
                        <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Email Address</FormLabel>
                        <Input
                          type="email"
                          value={email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                          placeholder="Enter your email address"
                          size="lg"
                          bg={isDark ? 'gray.700' : 'white'}
                          border="2px"
                          borderColor={isDark ? 'gray.600' : 'gray.200'}
                          color={isDark ? 'gray.300' : 'gray.800'}
                          _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                          _hover={{ borderColor: 'blue.300' }}
                          _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                        />
                        <FormErrorMessage>{errors.email}</FormErrorMessage>
                      </FormControl>
                    </VStack>

                    <Button
                      type="submit"
                      size="lg"
                      w="full"
                      bgGradient="linear(to-r, blue.400, purple.500)"
                      color="white"
                      isLoading={isLoading}
                      loadingText="Sending..."
                      _hover={{
                        bgGradient: 'linear(to-r, blue.500, purple.600)',
                        transform: 'translateY(-2px)',
                        shadow: 'lg'
                      }}
                      transition="all 0.2s"
                    >
                      Send Reset Email
                    </Button>

                    <Divider borderColor={isDark ? 'gray.600' : 'gray.200'} />

                    <HStack spacing={1}>
                      <Text color={isDark ? 'gray.400' : 'gray.600'} fontSize="sm">
                        Remember your password?
                      </Text>
                      <Link 
                        as={RouterLink} 
                        to="/login" 
                        color="blue.500"
                        fontWeight="medium"
                        _hover={{ color: 'blue.400' }}
                      >
                        Sign in
                      </Link>
                    </HStack>
                  </VStack>
                </form>
              )}

              {!isResetMode && isEmailSent && (
                <VStack spacing={6}>
                  <Alert
                    status="success"
                    variant="subtle"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    textAlign="center"
                    borderRadius="xl"
                    p={6}
                    bg={isDark ? 'green.900' : 'green.50'}
                    borderColor={isDark ? 'green.600' : 'green.200'}
                  >
                    <AlertIcon boxSize="40px" mr={0} />
                    <AlertTitle mt={4} mb={1} fontSize="lg">
                      Check your email!
                    </AlertTitle>
                    <AlertDescription maxWidth="sm">
                      We've sent password reset instructions to <strong>{email}</strong>. 
                      Please check your inbox and follow the link to reset your password.
                    </AlertDescription>
                  </Alert>

                  <VStack spacing={4} w="full">
                    <Button
                      leftIcon={<ArrowBackIcon />}
                      variant="outline"
                      size="lg"
                      w="full"
                      onClick={() => setIsEmailSent(false)}
                      borderColor={isDark ? 'gray.600' : 'gray.200'}
                      color={isDark ? 'gray.300' : 'gray.700'}
                      _hover={{
                        transform: 'translateY(-2px)',
                        shadow: 'lg',
                        bg: isDark ? 'gray.700' : 'gray.50'
                      }}
                      transition="all 0.2s"
                    >
                      Try Different Email
                    </Button>

                    <Link 
                      as={RouterLink} 
                      to="/login" 
                      color="blue.500"
                      fontWeight="medium"
                      _hover={{ color: 'blue.400' }}
                    >
                      Back to Sign In
                    </Link>
                  </VStack>
                </VStack>
              )}

              {isResetMode && (
                <form onSubmit={handleResetPassword}>
                  <VStack spacing={6}>
                    <VStack spacing={4} w="full">
                      <FormControl isRequired isInvalid={!!errors.password}>
                        <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>New Password</FormLabel>
                        <InputGroup>
                          <Input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => handleInputChange('password', e.target.value)}
                            placeholder="Enter your new password"
                            size="lg"
                            bg={isDark ? 'gray.700' : 'white'}
                            border="2px"
                            borderColor={isDark ? 'gray.600' : 'gray.200'}
                            color={isDark ? 'gray.300' : 'gray.800'}
                            _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                            _hover={{ borderColor: 'blue.300' }}
                            _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                          />
                          <InputRightElement h="full">
                            <IconButton
                              variant="ghost"
                              onClick={() => setShowPassword(!showPassword)}
                              icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                              aria-label="Toggle password visibility"
                              color={isDark ? 'gray.400' : 'gray.600'}
                            />
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>{errors.password}</FormErrorMessage>
                      </FormControl>

                      <FormControl isRequired isInvalid={!!errors.confirmPassword}>
                        <FormLabel color={isDark ? 'gray.300' : 'gray.800'}>Confirm New Password</FormLabel>
                        <InputGroup>
                          <Input
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                            placeholder="Confirm your new password"
                            size="lg"
                            bg={isDark ? 'gray.700' : 'white'}
                            border="2px"
                            borderColor={isDark ? 'gray.600' : 'gray.200'}
                            color={isDark ? 'gray.300' : 'gray.800'}
                            _placeholder={{ color: isDark ? 'gray.500' : 'gray.400' }}
                            _hover={{ borderColor: 'blue.300' }}
                            _focus={{ borderColor: 'blue.500', boxShadow: 'none' }}
                          />
                          <InputRightElement h="full">
                            <IconButton
                              variant="ghost"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                              aria-label="Toggle confirm password visibility"
                              color={isDark ? 'gray.400' : 'gray.600'}
                            />
                          </InputRightElement>
                        </InputGroup>
                        <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
                      </FormControl>
                    </VStack>

                    <Button
                      type="submit"
                      size="lg"
                      w="full"
                      bgGradient="linear(to-r, green.400, blue.500)"
                      color="white"
                      isLoading={isLoading}
                      loadingText="Updating password..."
                      _hover={{
                        bgGradient: 'linear(to-r, green.500, blue.600)',
                        transform: 'translateY(-2px)',
                        shadow: 'lg'
                      }}
                      transition="all 0.2s"
                    >
                      Update Password
                    </Button>

                    <Divider borderColor={isDark ? 'gray.600' : 'gray.200'} />

                    <Link 
                      as={RouterLink} 
                      to="/login" 
                      color="blue.500"
                      fontWeight="medium"
                      _hover={{ color: 'blue.400' }}
                    >
                      Back to Sign In
                    </Link>
                  </VStack>
                </form>
              )}
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
}; 